import { LocalizationManager } from '@lib/localization';
import {
  useAddFolderMutation,
  useLazyGetFolderByPathQuery,
  useUpdateFolderMutation
} from '@store/api/folderEndpoints';
import { useAddInspectionMutation } from '@store/api/inspectionEndpoints';
import { type PartialProjectFolderInfo, useAddProjectMutation, useLazyGetProjectQuery } from '@store/api/projectEndpoints';
import { useAppDispatch } from '@store/hooks';
import { showSnackbar } from '@store/reducers/snackbarSlice';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  buildTreeFromFiles,
  collectStats,
  getParentPath,
  readManifestFromSPProject,
  ROOT_ID,
  traverseDroppedEntry
} from '../tree/TreeBuilder';
import { type BulkTreeItem, type PendingRename, type ScannedFile, type UseBulkUploadOptions } from '../types';


export const useBulkUpload = ({ parentFolder }: UseBulkUploadOptions) => {
  const [tree, setTree] = useState<BulkTreeItem | null>(null);
  const [includeInspections, setIncludeInspections] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [currentStepLabel, setCurrentStepLabel] = useState('');
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);
  const [retryingItemId, setRetryingItemId] = useState<string | null>(null);

  // A rename awaiting user confirmation before it's persisted to the backend.
  const [pendingRename, setPendingRename] = useState<PendingRename | null>(null);

  const [addFolder] = useAddFolderMutation();
  const [addProject] = useAddProjectMutation();
  const [addInspection] = useAddInspectionMutation();
  const [updateFolder] = useUpdateFolderMutation();
  const [fetchFolderByPath] = useLazyGetFolderByPathQuery();
  const [fetchProject] = useLazyGetProjectQuery();
  const dispatch = useAppDispatch();

  // Resolved folder IDs keyed by relative path, e.g. "/batch-1" -> "65ab...".
  // Persisted across renders so retries can find the folder again.
  const folderIdByPath = useRef<Map<string, string | null>>(new Map());

  // Last successfully uploaded project ID per folder path. Inspections in the
  // same folder will be attached to that project.
  const projectIdByFolderPath = useRef<Map<string, string>>(new Map());

  const stats = useMemo(
    () => (tree ? collectStats(tree) : { folders: 0, projects: 0, inspections: 0 }),
    [tree],
  );

  // ───────────────────────────────────────────────────────────
  // 1. Scanning — turn a picked or dropped folder into a tree
  // ───────────────────────────────────────────────────────────

  // Builds the tree from a flat list of scanned files (shared by both inputs).
  const scanFiles = useCallback(
    async (files: ScannedFile[]) => {
      if (files.length === 0) {
        setError(LocalizationManager.t('Project_Bulk_Error_NoFiles'));
        return;
      }

      setScanning(true);
      setError(null);

      try {
        const built = buildTreeFromFiles(files);

        if (built.children.length === 0) {
          setError(LocalizationManager.t('Project_Bulk_Error_NoProjectFiles'));
          setTree(null);
          return;
        }

        setTree(built);
      } catch (err) {
        setError(err instanceof Error ? err.message : LocalizationManager.t('Project_Bulk_Error_ScanFailed'));
      } finally {
        setScanning(false);
      }
    },
    [],
  );

  // Entry point for the "select folder" input (uses webkitRelativePath).
  const scanFolderInput = useCallback((fileList: FileList) => {
    const scanned: ScannedFile[] = [];

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      const relativePath =
        (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
      scanned.push({ file, relativePath });
    }

    return scanFiles(scanned);
  }, [scanFiles]);

  // Entry point for drag & drop (walks the dropped directory entries).
  const scanDroppedItems = useCallback(async (items: DataTransferItemList) => {
    setScanning(true);

    try {
      const scanned: ScannedFile[] = [];

      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry();
        if (entry) scanned.push(...await traverseDroppedEntry(entry, ''));
      }

      await scanFiles(scanned);
    } catch (err) {
      setError(err instanceof Error ? err.message : LocalizationManager.t('Project_Bulk_Error_ReadDroppedFailed'));
      setScanning(false);
    }
  }, [scanFiles]);

  // ───────────────────────────────────────────────────────────
  // 2. Selection — let the user pick what to upload
  // ───────────────────────────────────────────────────────────

  // Toggles selection on an item. For folders, cascades the new value to all
  // descendants so unchecking a folder excludes its whole subtree from upload.
  const toggleItemSelection = useCallback((itemId: string) => {
    setTree(previousTree => {
      if (!previousTree) return previousTree;

      const findTarget = (item: BulkTreeItem): BulkTreeItem | null => {
        if (item.id === itemId) return item;
        for (const child of item.children) {
          const found = findTarget(child);
          if (found) return found;
        }
        return null;
      };

      const target = findTarget(previousTree);
      if (!target) return previousTree;

      const nextValue = !target.selected;

      const applyToSubtree = (item: BulkTreeItem): BulkTreeItem => ({
        ...item,
        selected: nextValue,
        children: item.children.map(applyToSubtree),
      });

      const apply = (item: BulkTreeItem): BulkTreeItem => {
        if (item.id === itemId) return applyToSubtree(item);
        return { ...item, children: item.children.map(apply) };
      };

      return apply(previousTree);
    });
  }, []);

  // ───────────────────────────────────────────────────────────
  // 3. Single-item upload — building blocks used by the orchestrator
  // ───────────────────────────────────────────────────────────

  // Immutably updates a single item in the tree by id, then re-sets state so
  // React re-renders that row (status badge, error message, etc.).
  const updateTreeItem = useCallback((itemId: string, updates: Partial<BulkTreeItem>) => {
    setTree(previousTree => {
      if (!previousTree) return previousTree;

      const apply = (item: BulkTreeItem): BulkTreeItem => {
        if (item.id === itemId) return { ...item, ...updates };
        return { ...item, children: item.children.map(apply) };
      };

      return apply(previousTree);
    });
  }, []);

  // Applies the same update to every descendant of a folder (but not the folder
  // itself). Used to cascade "skipped"/"error" down a whole subtree at once.
  const updateDescendants = useCallback((folderId: string, updates: Partial<BulkTreeItem>) => {
    setTree(previousTree => {
      if (!previousTree) return previousTree;

      const applyToAll = (item: BulkTreeItem): BulkTreeItem => ({
        ...item,
        ...updates,
        children: item.children.map(applyToAll),
      });

      const apply = (item: BulkTreeItem): BulkTreeItem => {
        if (item.id === folderId) return { ...item, children: item.children.map(applyToAll) };
        return { ...item, children: item.children.map(apply) };
      };

      return apply(previousTree);
    });
  }, []);

  // Rewrites a folder's name and the paths/ids of its whole subtree locally.
  const applyLocalRename = useCallback((itemId: string, newName: string) => {
    setTree(previousTree => {
      if (!previousTree) return previousTree;

      const rename = (item: BulkTreeItem): BulkTreeItem => {
        if (item.id === itemId) {
          if (item.name === newName) return item;
          const newPath = `${getParentPath(item.path)}/${newName}`;
          return { ...rewriteSubtreePaths(item, item.path, newPath), name: newName };
        }
        return { ...item, children: item.children.map(rename) };
      };

      return rename(previousTree);
    });
  }, []);

  // Entry point for the rename UI. A folder that hasn't been uploaded yet is
  // renamed locally right away (it's just selection-phase editing). A folder
  // that already exists on the backend needs confirmation first, since renaming
  // it updates the server (and cascades to its subfolders) — see confirmRename.
  const renameItem = useCallback((itemId: string, rawName: string) => {
    const newName = rawName.trim();
    if (!newName || !tree) return;

    const located = locateItem(tree, itemId);
    if (!located || located.node.type !== 'folder' || located.node.name === newName) return;

    if (located.node.remoteFolderId) {
      setPendingRename({ itemId, oldName: located.node.name, newName });
      return;
    }

    applyLocalRename(itemId, newName);
  }, [applyLocalRename, tree]);

  // Confirms a pending rename: updates the folder locally and on the backend.
  const confirmRename = useCallback(async () => {
    if (!pendingRename || !tree) return;

    const { itemId, newName } = pendingRename;
    setPendingRename(null);

    const located = locateItem(tree, itemId);
    const remoteFolderId = located?.node.remoteFolderId;
    if (!located || !remoteFolderId) return;

    const { node, parent } = located;
    const newId = `${getParentPath(node.path)}/${newName}`;

    applyLocalRename(itemId, newName);

    const parentBackendId =
      parent && parent.id !== ROOT_ID ? parent.remoteFolderId ?? null : parentFolder?._id ?? null;

    updateTreeItem(newId, { status: 'uploading', error: undefined });

    try {
      await updateFolder({ _id: remoteFolderId, name: newName, parentFolder: parentBackendId }).unwrap();
      updateTreeItem(newId, { status: 'success' });
    } catch (err) {
      const message = err instanceof Error ? err.message : LocalizationManager.t('Project_Bulk_Error_RenameFailed');
      updateTreeItem(newId, { status: 'error', error: message });
    }
  }, [applyLocalRename, parentFolder?._id, pendingRename, tree, updateFolder, updateTreeItem]);

  const cancelRename = useCallback(() => setPendingRename(null), []);

  // Looks up the backend _id of a just-created folder by its path.
  const resolveFolderIdFromPath = useCallback(
    async (relativePath: string, fallbackId: string | null): Promise<string | null> => {
      try {
        const parentPrefix = parentFolder?.path && parentFolder.path !== '/' ? parentFolder.path : '';
        const absolutePath = `${parentPrefix}${relativePath}` || '/';
        const folder = await fetchFolderByPath(absolutePath).unwrap();
        return folder?._id ?? fallbackId;
      } catch {
        return fallbackId;
      }
    },
    [fetchFolderByPath, parentFolder?.path],
  );

  // Creates one folder on the backend and remembers its resolved id. Returns
  // whether creation succeeded so the caller can decide what to do with the
  // folder's children. parentPath / selfPath are *effective* paths (built from
  // selected folders only — see processSubtree), so they match where the folder
  // actually lands on the backend once unselected ancestors are flattened out.
  const uploadFolderItem = useCallback(
    async (folderItem: BulkTreeItem, parentPath: string, selfPath: string): Promise<boolean> => {
      setCurrentStepLabel(LocalizationManager.t('Project_Bulk_Step_CreatingFolder', [folderItem.name]));
      updateTreeItem(folderItem.id, { status: 'uploading', error: undefined });

      try {
        const parentId = folderIdByPath.current.get(parentPath) ?? parentFolder?._id ?? null;
        await addFolder({ name: folderItem.name, parentFolder: parentId }).unwrap();

        const newId = await resolveFolderIdFromPath(selfPath, parentId);
        folderIdByPath.current.set(selfPath, newId);

        updateTreeItem(folderItem.id, { status: 'success', remoteFolderId: newId ?? undefined });
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : LocalizationManager.t('Project_Bulk_Error_FolderCreateFailed');
        updateTreeItem(folderItem.id, { status: 'error', error: message });
        return false;
      }
    },
    [addFolder, parentFolder?._id, resolveFolderIdFromPath, updateTreeItem],
  );

  // Uploads one .spproject (with its embedded inspections, if included).
  const uploadProjectItem = useCallback(
    async (projectItem: BulkTreeItem, parentPath: string) => {
      if (!projectItem.file) return;

      setCurrentStepLabel(LocalizationManager.t('Project_Bulk_Step_UploadingProject', [projectItem.name]));
      updateTreeItem(projectItem.id, { status: 'uploading', error: undefined });

      try {
        const containingFolderId =
          folderIdByPath.current.get(parentPath) ?? parentFolder?._id ?? null;

        const manifest = await readManifestFromSPProject(projectItem.file);

        const existingProjectResponse = await fetchProject(manifest.projectInfo.id);

        if (existingProjectResponse?.isSuccess) {
          const projectInfo = existingProjectResponse.data;
          const projectParentFolderPath = (projectInfo.parentFolder as PartialProjectFolderInfo)?.path ?? '';
          const projectPath = `${projectParentFolderPath}/${projectInfo.projectInfo.name}.spproject`;
          const message = LocalizationManager.t('Project_Form_AlreadyExist_Error', [projectPath]);

          dispatch(showSnackbar({ severity: 'error', message }));

          throw new Error(projectPath);
        };

        const inspectionIds = includeInspections
          ? (manifest.inspections ?? [])
            .filter(inspection => !inspection.isTemplate)
            .map(inspection => inspection.id)
          : [];

        const formData = new FormData();
        formData.append('file', projectItem.file);
        formData.append('inspectionIds', JSON.stringify(inspectionIds));
        if (containingFolderId) formData.append('parentFolder', containingFolderId);

        await addProject(formData).unwrap();

        projectIdByFolderPath.current.set(parentPath, manifest.projectInfo.id);
        updateTreeItem(projectItem.id, { status: 'success', projectId: manifest.projectInfo.id });
      } catch (err) {
        const message = err instanceof Error ? err.message : LocalizationManager.t('Project_Bulk_Error_ProjectUploadFailed');
        updateTreeItem(projectItem.id, { status: 'error', error: message });
      }
    },
    [addProject, dispatch, fetchProject, includeInspections, parentFolder?._id, updateTreeItem],
  );

  // Uploads one standalone .spinspection, attaching it to the folder's project.
  const uploadInspectionItem = useCallback(
    async (inspectionItem: BulkTreeItem, parentPath: string) => {
      if (!inspectionItem.file) return;

      const projectId = projectIdByFolderPath.current.get(parentPath);

      if (!projectId) {
        updateTreeItem(inspectionItem.id, {
          status: 'error',
          error: LocalizationManager.t('Project_Bulk_Error_NoProjectForInspection'),
        });
        return;
      }

      setCurrentStepLabel(LocalizationManager.t('Project_Bulk_Step_UploadingInspection', [inspectionItem.name]));
      updateTreeItem(inspectionItem.id, { status: 'uploading', error: undefined });

      try {
        const formData = new FormData();
        formData.append('file', inspectionItem.file);
        await addInspection({ projectId, formData }).unwrap();

        updateTreeItem(inspectionItem.id, { status: 'success' });
      } catch (err) {
        const message = err instanceof Error ? err.message : LocalizationManager.t('Project_Bulk_Error_InspectionUploadFailed');
        updateTreeItem(inspectionItem.id, { status: 'error', error: message });
      }
    },
    [addInspection, updateTreeItem],
  );

  // ───────────────────────────────────────────────────────────
  // 4. Orchestration — walk the tree and run the whole upload
  // ───────────────────────────────────────────────────────────

  // Flattens the tree into a list so we can count the total upload steps.
  const flattenForProgress = useCallback((root: BulkTreeItem): BulkTreeItem[] => {
    const flat: BulkTreeItem[] = [];
    const visit = (item: BulkTreeItem) => {
      if (item !== root) flat.push(item);
      item.children.forEach(visit);
    };
    visit(root);
    return flat;
  }, []);

  // Walks a subtree depth-first: creates each *selected* folder, then uploads
  // its selected projects and inspections. An unselected folder is NOT created,
  // but we still descend into it — any selected items inside are "promoted" and
  // uploaded into the nearest selected ancestor (or the root target if none).
  // Everything unselected is marked skipped.
  //  - effectiveParentPath is the path of the nearest created ancestor ('' = the
  //    root target). It's built from selected folders only, so it matches where
  //    items actually land on the backend once unselected folders are flattened.
  //  - onStep is called once per processed item so progress can be tracked.
  //  - skipSucceeded leaves already-uploaded items alone (used on retry).
  const processSubtree = useCallback(
    async (
      item: BulkTreeItem,
      isRoot: boolean,
      effectiveParentPath: string,
      options: { onStep?: () => void; skipSucceeded?: boolean } = {},
    ): Promise<void> => {
      const { onStep, skipSucceeded = false } = options;

      // The folder this item's children upload into: the nearest created
      // ancestor by default, or this folder itself once we create it.
      let containingFolderPath = effectiveParentPath;

      if (!isRoot && item.type === 'folder') {
        if (item.selected) {
          const effectivePath = `${effectiveParentPath}/${item.name}`;
          containingFolderPath = effectivePath;

          if (!(skipSucceeded && item.status === 'success')) {
            const created = await uploadFolderItem(item, effectiveParentPath, effectivePath);
            onStep?.();

            if (!created) {
              // The folder doesn't exist on the backend, so its children have
              // nowhere to go — mark the whole subtree failed rather than
              // silently uploading them into an ancestor folder.
              updateDescendants(item.id, {
                status: 'error',
                error: LocalizationManager.t('Project_Bulk_Error_ParentFolderFailed'),
              });
              for (let i = 0; i < countSelectedDescendants(item); i++) onStep?.();
              return;
            }
          }
        } else {
          // Unselected folder: don't create it, just mark the row skipped. We
          // still walk into it (containingFolderPath stays the nearest created
          // ancestor), so a selected project under an unselected folder is
          // uploaded straight into that ancestor instead of being skipped too.
          updateTreeItem(item.id, { status: 'skipped' });
        }
      }

      const childFolders = item.children.filter(child => child.type === 'folder');
      const childProjects = item.children.filter(child => child.type === 'project');
      const childInspections = item.children.filter(child => child.type === 'inspection');

      for (const childFolder of childFolders) {
        await processSubtree(childFolder, false, containingFolderPath, options);
      }

      for (const project of childProjects) {
        if (!project.selected) {
          updateTreeItem(project.id, { status: 'skipped' });
          continue;
        }
        if (skipSucceeded && project.status === 'success') continue;
        await uploadProjectItem(project, containingFolderPath);
        onStep?.();
      }

      for (const inspection of childInspections) {
        if (!inspection.selected) {
          updateTreeItem(inspection.id, { status: 'skipped' });
          continue;
        }
        if (skipSucceeded && inspection.status === 'success') continue;
        await uploadInspectionItem(inspection, containingFolderPath);
        onStep?.();
      }
    },
    [updateDescendants, updateTreeItem, uploadFolderItem, uploadInspectionItem, uploadProjectItem],
  );

  // Runs the full upload from the root, skipping unchecked items and tracking
  // progress.
  const startUpload = useCallback(async () => {
    if (!tree) return;

    setUploading(true);
    setError(null);
    setUploadComplete(false);

    folderIdByPath.current = new Map();
    folderIdByPath.current.set('', parentFolder?._id ?? null);
    projectIdByFolderPath.current = new Map();

    const totalCount = flattenForProgress(tree).filter(item => item.selected).length;
    setProgress({ done: 0, total: totalCount });

    let done = 0;
    const advanceProgress = () => {
      done++;
      setProgress({ done, total: totalCount });
    };

    try {
      await processSubtree(tree, true, '', { onStep: advanceProgress });
      setCurrentStepLabel(LocalizationManager.t('Project_Bulk_Step_Finished'));
      setUploadComplete(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : LocalizationManager.t('Project_Bulk_Error_UploadFailed'));
    } finally {
      setUploading(false);
    }
  }, [flattenForProgress, parentFolder?._id, processSubtree, tree]);

  // ───────────────────────────────────────────────────────────
  // 5. Retry & reset
  // ───────────────────────────────────────────────────────────

  // Re-runs the upload for a single failed item (used by the retry button).
  // Retrying a folder re-creates it and (re)uploads everything beneath it,
  // leaving anything that already succeeded untouched.
  const retryItem = useCallback(async (item: BulkTreeItem) => {
    if (retryingItemId || !tree) return;
    setRetryingItemId(item.id);

    try {
      // The recursive effective-path context isn't available here, so derive the
      // nearest selected ancestor's path the same way processSubtree would.
      const effectiveParentPath = findEffectiveParentPath(tree, item.id);

      if (item.type === 'folder') {
        await processSubtree(item, false, effectiveParentPath, { skipSucceeded: true });
      } else if (item.type === 'project') {
        await uploadProjectItem(item, effectiveParentPath);
      } else {
        await uploadInspectionItem(item, effectiveParentPath);
      }
    } finally {
      setRetryingItemId(null);
    }
  }, [processSubtree, retryingItemId, tree, uploadInspectionItem, uploadProjectItem]);

  // Clears everything back to the initial state (called when the modal closes).
  const reset = useCallback(() => {
    setTree(null);
    setIncludeInspections(true);
    setScanning(false);
    setUploading(false);
    setUploadComplete(false);
    setCurrentStepLabel('');
    setProgress({ done: 0, total: 0 });
    setError(null);
    setRetryingItemId(null);
    setPendingRename(null);
    folderIdByPath.current = new Map();
    projectIdByFolderPath.current = new Map();
  }, []);

  return {
    tree,
    stats,
    includeInspections,
    setIncludeInspections,
    scanning,
    uploading,
    uploadComplete,
    currentStepLabel,
    progress,
    error,
    retryingItemId,
    scanFolderInput,
    scanDroppedItems,
    startUpload,
    retryItem,
    toggleItemSelection,
    renameItem,
    pendingRename,
    confirmRename,
    cancelRename,
    reset,
  };
};

// Finds an item by id and returns it together with its parent (needed to
// resolve the backend parentFolder id when renaming).
const locateItem = (
  root: BulkTreeItem,
  itemId: string,
  parent: BulkTreeItem | null = null,
): { node: BulkTreeItem; parent: BulkTreeItem | null } | null => {
  if (root.id === itemId) return { node: root, parent };
  for (const child of root.children) {
    const found = locateItem(child, itemId, root);
    if (found) return found;
  }
  return null;
};

// Counts the selected descendants of an item (excluding the item itself). Used
// to keep the progress bar consistent when a whole subtree is skipped at once.
const countSelectedDescendants = (item: BulkTreeItem): number =>
  item.children.reduce(
    (total, child) => total + (child.selected ? 1 : 0) + countSelectedDescendants(child),
    0,
  );

// Returns the effective parent path of `itemId` — the path built from only the
// *selected* ancestor folders (unselected ones are flattened out, exactly as
// processSubtree does). '' means the root upload target. Used on retry, where
// the recursive effective-path context isn't available.
const findEffectiveParentPath = (root: BulkTreeItem, itemId: string): string => {
  const walk = (item: BulkTreeItem, effectiveParent: string): string | null => {
    for (const child of item.children) {
      if (child.id === itemId) return effectiveParent;
      if (child.type === 'folder') {
        const childEffective = child.selected ? `${effectiveParent}/${child.name}` : effectiveParent;
        const found = walk(child, childEffective);
        if (found !== null) return found;
      }
    }
    return null;
  };

  return walk(root, '') ?? '';
};

// Rewrites the path/id of a renamed folder and all of its descendants by
// swapping the old path prefix for the new one. id === path for every node
// (see TreeBuilder), so both are derived from the same string.
const rewriteSubtreePaths = (
  item: BulkTreeItem,
  oldPrefix: string,
  newPrefix: string,
): BulkTreeItem => {
  const path = newPrefix + item.path.slice(oldPrefix.length);
  return {
    ...item,
    id: path,
    path,
    children: item.children.map(child => rewriteSubtreePaths(child, oldPrefix, newPrefix)),
  };
};

export { ROOT_ID };
